import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { EmbedSignDocument } from "@documenso/embed-react";

import loadEversign from "./loadEversign";
import AnimatedButton from "../../components/AnimatedButton";
import { Title, IframeWrapperDiv } from "../../styled";

const Container = styled.div.attrs({
  className: "pa3 ph4-m pv3-m pv5-l ph6-l",
})`
  @media screen and (min-width: 70em) {
    background-image: url("/img/london.svg");
    z-index: -1;
    background-repeat: no-repeat;
    background-size: 40% 40%;
    background-position: 400px 120px;
  }
`;

const Esignature = styled.div.attrs({
  className: "w-100 overflow-hidden absolute ba b--black-80",
})`
  height: 88%;
  width: 100%;
`;

const Subheading = styled.div.attrs({
  className: "f6 lh-copy mt3 pv3 ph3 br2 flex",
})`
  color: #333;
  background-color: #e6f7ff;
  border: 1px solid #1890ff;
  border-radius: 4px;
`;

const InfoIcon = styled.span.attrs({
  className: "mr2",
})`
  color: #1890ff;
  font-size: 1.2rem;
  align-self: flex-start;
  margin-top: -0.2rem;
`;

export function Headline({ organizationName, withholdPremium, withholdPremiumOrgOverride, jobWithholdPremium, isGeneralOptIn }) {
  const showPremiumRemittance = withholdPremium || withholdPremiumOrgOverride || jobWithholdPremium;
  
  return (
    <Container>
      <Title>
        {isGeneralOptIn ? (
          <>
            Review your
            <br /> application
          </>
        ) : (
          <>
            Review your
            <br /> quote
          </>
        )}
      </Title>
      {showPremiumRemittance && organizationName && (
        <Subheading>
          <InfoIcon>📣</InfoIcon>
          <div>
            <b>{organizationName}</b> will remit the premium payments for this coverage on your behalf. As a result, you won't be asked to pay during this application.
          </div>
        </Subheading>
      )}
      {showPremiumRemittance && isGeneralOptIn && !organizationName && (
        <Subheading>
          <InfoIcon>📣</InfoIcon>
          <div>
            Premium payments for this coverage will be remitted on your behalf. As a result, you won't be asked to pay during this application.
          </div>
        </Subheading>
      )}
    </Container>
  );
}

export function ReviewQuoteForm(props) {
  const {
    stepForward,
    // Injected by react-step-wizard: true only while this is the current step.
    isActive,
    esignEmbedUrl,
    isEsignatureComplete,
    withholdPremium,
    withholdPremiumOrgOverride,
    organizationName,
    esignProvider = "eversign"  // NEW: provider detection
  } = props;

  const [isComplete, setComplete] = useState(isEsignatureComplete);
  const [loaded, setLoaded] = useState(false);
  const handle = useFullScreenHandle();

  // The e-sign "signed" callback, the Documenso completion callback, and the
  // manual "Continue" button can all advance the wizard. We funnel them through
  // a single `advance` that only fires while THIS step is active: a stray
  // callback firing after the wizard has already moved on (the original
  // orphaning vector—stepForward landing on FINAL_REVIEW and completing without
  // a bind) is ignored instead of stepping the session forward again.
  //
  // We do NOT latch here. Deduping concurrent advances (auto-advance racing a
  // click) is owned by the Wizard's in-flight guard on stepForward, which is
  // tied directly to the mutation lifecycle—so it can't be stranded by a
  // failed/skipped mutation, and there's no second ref whose update timing
  // could open a duplicate-advance window. Live values are read through refs so
  // the long-lived Eversign callback never closes over a stale isActive value.
  const isActiveRef = useRef(isActive);
  const stepForwardRef = useRef(stepForward);
  // useFullScreenHandle() returns a new object whenever fullscreen toggles, so
  // we read it through a ref to keep it out of the embed-setup effect's deps
  // (otherwise entering/exiting fullscreen would tear down and reopen the
  // document).
  const handleRef = useRef(handle);

  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);
  useEffect(() => {
    stepForwardRef.current = stepForward;
  }, [stepForward]);
  useEffect(() => {
    handleRef.current = handle;
  }, [handle]);

  const advance = useCallback(() => {
    if (!isActiveRef.current) return;
    stepForwardRef.current();
  }, []);

  useEffect(() => {
    // Load Eversign SDK only when needed
    if (esignProvider === "eversign") {
      loadEversign(() => {
        setLoaded(true);
      });
    } else {
      // Documenso uses React SDK - no external script needed
      setLoaded(true);
    }

    return function cleanup() {
      setLoaded(false);
    };
  }, [esignProvider]);

  // Open the Eversign embed when this step is showing. Guard against reopening
  // within a single visit, but DO reopen when the contractor leaves and returns
  // (StepWizard keeps steps mounted, so without reopening the container would
  // sit empty on return). Registering the callbacks here—rather than in the
  // render body—keeps them from being re-bound on every render.
  const eversignOpenedRef = useRef(false);

  useEffect(() => {
    if (esignProvider !== "eversign") return;
    if (!isActive) return;
    if (!esignEmbedUrl || !loaded) return;
    if (eversignOpenedRef.current) return;
    if (!window.eversign || !window.eversign.open) return;

    eversignOpenedRef.current = true;
    window.eversign.open({
      url: esignEmbedUrl,
      containerID: "eversign",
      width: "100%",
      height: "100%",
      events: {
        loaded: function () {
          console.log("[Eversign] Document loaded");
        },
        signed: function () {
          console.log("[Eversign] Document signed");
          setComplete(true);
          advance();
          handleRef.current.exit();
        },
        declined: function () {
          console.log("[Eversign] Document declined");
          handleRef.current.exit();
        },
        error: function () {
          console.log("[Eversign] Error occurred");
          handleRef.current.exit();
        },
      },
    });

    return () => {
      // Leaving the step (isActive -> false, or unmount) releases the guard so
      // the embed reopens on the next visit.
      eversignOpenedRef.current = false;
    };
  }, [esignProvider, isActive, esignEmbedUrl, loaded, advance]);

  // Documenso SDK callbacks
  const handleDocumensoCompleted = () => {
    console.log("[Documenso] Document signed successfully");
    setComplete(true);
    handle.exit();
    // Call stepForward directly - no timeout needed
    advance();
  };

  const handleDocumensoReady = () => {
    console.log("[Documenso] Document ready for signing");
  };

  const handleDocumensoError = (error) => {
    console.error("[Documenso] Error occurred:", error);
    handle.exit();
    // TODO: Show error message to user
    alert("An error occurred while loading the document. Please try again or contact support.");
  };

  // Extract signing token and host from embed URL for Documenso SDK
  // URL format: https://documenso.1099policy.com/sign/{token}[?queryParams]
  const getDocumensoConfig = () => {
    // Only extract token for Documenso provider to avoid console errors for Eversign users
    if (esignProvider !== "documenso") {
      return { token: null, host: null };
    }
    
    if (!esignEmbedUrl) {
      console.warn("[Documenso] No embed URL provided");
      return { token: null, host: null };
    }
    
    const parts = esignEmbedUrl.split("/sign/");
    if (parts.length < 2) {
      console.error("[Documenso] Invalid URL format, expected /sign/ in URL:", esignEmbedUrl);
      return { token: null, host: null };
    }
    
    // Extract host from the first part (e.g., "https://documenso.1099policy.com")
    const host = parts[0];
    
    // Remove query parameters and hash fragments from token
    const tokenWithParams = parts[1];
    const token = tokenWithParams.split('?')[0].split('#')[0].trim();
    
    if (!token) {
      console.error("[Documenso] Token is empty after extraction from URL:", esignEmbedUrl);
      return { token: null, host: null };
    }
    
    console.log("[Documenso] Successfully extracted token and host:", host);
    return { token, host };
  };

  const { token: documensoToken, host: documensoHost } = getDocumensoConfig();
  const [tokenError, setTokenError] = useState(null);

  useEffect(() => {
    // Handle Documenso provider and unknown providers (anything not "eversign")
    if (esignProvider !== "eversign") {
      if (esignProvider !== "documenso") {
        // Unknown provider - show error
        setTokenError(
          `Invalid e-signature provider: "${esignProvider}". Please contact support.`
        );
      } else if (!documensoToken) {
        // Documenso provider but no token
        setTokenError("Unable to load signing document. Please contact support.");
      } else {
        // Clear error when valid token arrives
        setTokenError(null);
      }
    } else {
      // Eversign provider - clear any previous errors
      setTokenError(null);
    }
  }, [esignProvider, documensoToken]);


  return (
    <Fragment>
      <IframeWrapperDiv>
        <div className="flex-auto">
          <div>
            <FullScreen handle={handle}>
              {esignProvider === "eversign" ? (
                <Esignature id="eversign"></Esignature>
              ) : (
                <Esignature id="documenso">
                  {tokenError ? (
                    <div className="flex items-center justify-center h-100 pa4">
                      <div className="tc">
                        <div className="f3 fw6 mb3 dark-red">⚠️ Error Loading Document</div>
                        <div className="f5 lh-copy mb3">{tokenError}</div>
                        <button
                          className="f6 link br2 ph3 pv2 dib pointer white bg-blue ba b--blue"
                          onClick={() => window.location.reload()}
                        >
                          Retry
                        </button>
                      </div>
                    </div>
                  ) : documensoToken ? (
                    <EmbedSignDocument
                      className="w-100 h-100"
                      token={documensoToken}
                      host={documensoHost}
                      onDocumentReady={handleDocumensoReady}
                      onDocumentCompleted={handleDocumensoCompleted}
                      onDocumentError={handleDocumensoError}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-100">
                      <div className="tc">
                        <div className="f3 fw6 mb3">Loading document...</div>
                        <div className="loading-spinner"></div>
                      </div>
                    </div>
                  )}
                </Esignature>
              )}
            </FullScreen>
          </div>
          <div className="fixed bottom-0 bottom--1-ns right-0">
            <button
              className="f6 link br2 ph3 pv2 mb2 dib pointer animate mr2 black bg-white b--black ba"
              onClick={handle.enter}
            >
              Enter fullscreen
            </button>
            <AnimatedButton
              id="review-quote-continue"
              isDisabled={!isComplete}
              onClick={advance}
              primary
            >
              Continue
            </AnimatedButton>
          </div>
        </div>
      </IframeWrapperDiv>
    </Fragment>
  );
}
